/* eslint-disable react/no-array-index-key */
/* eslint-disable no-plusplus */
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import { format, isValid } from "date-fns";
import get from "lodash-es/get";

// Material
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";

// Config
import colors from "src/config/colors";

// Components
import Text from "src/components/Text";

const useStyles = makeStyles(theme => ({
  h3: { fontSize: "18px" },
  h4: { fontSize: "16px" },
  body: { fontSize: "14px" },
  listWrapper: {
    margin: 0,
    padding: theme.spacing(0, 3)
  },
  listItem: {
    fontSize: "14px"
  },
  value: {
    color: colors.pictonBlue,
    fontWeight: "600",
    fontSize: "14px"
  }
}));

const DATE_FMT = "dd/MM/yyyy";
const DATETIME_FMT = "dd/MM/yyyy HH:mm";
const TIME_FMT = "HH:mm";

function toDateSafe(v) {
  if (!v) return null;

  if (v instanceof Date) return isValid(v) ? v : null;

  if (typeof v === "object" && typeof v.toDate === "function") {
    const d = v.toDate();
    return d instanceof Date && isValid(d) ? d : null;
  }

  return null;
}

function formatBySchemaType(raw, schemaType) {
  const d = toDateSafe(raw);
  if (!d) return raw == null ? "" : String(raw);

  switch (schemaType) {
    case "date":
      return format(d, DATE_FMT);
    case "dateTime":
      return format(d, DATETIME_FMT);
    case "time":
      return format(d, TIME_FMT);
    default:
      return String(raw);
  }
}

function resolveSafeValue(raw, key, schemaType) {
  if (raw == null) return key;

  if (
    schemaType === "date" ||
    schemaType === "dateTime" ||
    schemaType === "time"
  ) {
    return formatBySchemaType(raw, schemaType);
  }

  if (typeof raw === "object") {
    return JSON.stringify(raw);
  }

  return String(raw);
}

function parseInlineToNodes(text, ctx, properties, classes) {
  const nodes = [];
  const regex = /(\{\{.*?\}\}|\*\*.*?\*\*|\*.*?\*)/g;
  let lastIndex = 0;
  let match;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    match = regex.exec(text);
    if (!match) break;

    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];

    if (token.startsWith("{{")) {
      const key = token.slice(2, -2).trim();
      const raw = get(ctx, key);
      const schemaType = properties?.[key]?.type;

      const safeVal = resolveSafeValue(raw, key, schemaType);

      nodes.push(
        <React.Fragment key={`${match.index}-var`}>
          [
          <Box component="span" className={classes.value}>
            {safeVal}
          </Box>
          ]
        </React.Fragment>
      );
    } else if (token.startsWith("**")) {
      const inner = token.slice(2, -2);
      nodes.push(
        <strong key={`${match.index}-b`}>
          {parseInlineToNodes(inner, ctx, properties, classes)}
        </strong>
      );
    } else if (token.startsWith("*")) {
      const inner = token.slice(1, -1);
      nodes.push(
        <em key={`${match.index}-i`}>
          {parseInlineToNodes(inner, ctx, properties, classes)}
        </em>
      );
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export function parseMarkdownToElements(md, ctx, properties, classes = {}) {
  const lines = md.split("\n");
  const out = [];
  let listOpen = false;
  let listItems = [];
  let keyCounter = 0;

  const flushList = () => {
    if (!listOpen) return;
    out.push(
      <Box
        component="ul"
        className={classes.listWrapper}
        key={`ul-${keyCounter++}`}
      >
        {listItems.map((li, i) => (
          <Text component="li" className={classes.listItem} key={`li-${i}`}>
            {li}
          </Text>
        ))}
      </Box>
    );
    listOpen = false;
    listItems = [];
  };

  lines.forEach(raw => {
    const line = raw.trim();
    if (!line) {
      flushList();
      return;
    }

    if (line.startsWith("# ")) {
      flushList();
      out.push(
        <Text type="heading3" className={classes.h3} key={`h1-${keyCounter++}`}>
          {parseInlineToNodes(
            line.replace(/^# /, ""),
            ctx,
            properties,
            classes
          )}
        </Text>
      );
      return;
    }

    if (line.startsWith("## ")) {
      flushList();
      out.push(
        <Text type="heading4" className={classes.h4} key={`h2-${keyCounter++}`}>
          {parseInlineToNodes(
            line.replace(/^## /, ""),
            ctx,
            properties,
            classes
          )}
        </Text>
      );
      return;
    }

    if (line.startsWith("### ")) {
      flushList();

      out.push(
        <Text type="heading5" className={classes.h5} key={`h3-${keyCounter++}`}>
          {parseInlineToNodes(
            line.replace(/^### /, ""),
            ctx,
            properties,
            classes
          )}
        </Text>
      );

      return;
    }

    if (/^[-*]\s+/.test(line)) {
      if (!listOpen) listOpen = true;

      listItems.push(
        parseInlineToNodes(
          line.replace(/^[-*]\s+/, ""),
          ctx,
          properties,
          classes
        )
      );

      return;
    }

    if (/^---+$/.test(line)) {
      flushList();

      out.push(
        <Divider className={classes.divider} key={`divider-${keyCounter++}`} />
      );

      return;
    }

    flushList();
    out.push(
      <Text type="body" className={classes.body} key={`p-${keyCounter++}`}>
        {parseInlineToNodes(line, ctx, properties, classes)}
      </Text>
    );
  });

  flushList();
  return out;
}

const MarkdownRenderer = ({
  schema: { StatementText, properties },
  values
}) => {
  const classes = useStyles();

  return parseMarkdownToElements(StatementText, values, properties, classes);
};

MarkdownRenderer.propTypes = {
  schema: PropTypes.shape({
    StatementText: PropTypes.string,
    properties: PropTypes.shape({})
  }).isRequired,
  values: PropTypes.shape({}).isRequired
};

export default MarkdownRenderer;
